import * as React from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetColumnAnalysesQuery, useDeleteColumnAnalysisMutation } from "@/modules/snapshots/businessReports.api";
import { ColumnAnalysisResponse } from "@/modules/snapshots/businessReports.types";
import { Trash2 } from "lucide-react";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface Props {
  snapshotCollectionId: string;
  subcollectionId?: string;
  fieldName: string;
  onSelect: (analysis: ColumnAnalysisResponse) => void;
  contractFamilyProjection?: boolean;
}

export const SavedAnalyses: React.FC<Props> = ({
  snapshotCollectionId,
  subcollectionId,
  fieldName,
  onSelect,
  contractFamilyProjection = false,
}) => {
  const { data: analyses, isLoading } = useGetColumnAnalysesQuery({
    snapshot_collection_id: snapshotCollectionId,
    subcollection_id: subcollectionId ?? null,
    field_name: fieldName,
    contract_family_projection: contractFamilyProjection,
  });
  const [deleteAnalysis, { isLoading: isDeleting }] = useDeleteColumnAnalysisMutation();
  const { formatDateTime } = useDateFormatter();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const handleDeleteClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteAnalysis(deleteId).unwrap();
      setDeleteId(null);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete analysis. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
      </div>
    );
  }

  if (!analyses || analyses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm text-gray-600">No saved analyses found</p>
        <p className="text-xs text-gray-400">Generate and save an analysis to see it here</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 p-4">
        {analyses.map(analysis => (
          <div
            key={analysis.id}
            onClick={() => onSelect(analysis)}
            className="cursor-pointer rounded-lg border border-gray-300 p-3 transition-colors hover:bg-gray-50"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="line-clamp-2 flex-1 text-sm">{analysis.prompt}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={e => handleDeleteClick(analysis.id, e)}
                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">{formatDateTime(analysis.created_at)}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!deleteId} onOpenChange={open => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Analysis</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this analysis? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" disabled={isDeleting}>
                Cancel
              </Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                "Deleting..."
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
